import React, { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { GlassCard } from '@/components/ui/GlassCard';

interface Comparison {
  loanProgram: USER_LOAN_PROGRAM;
  downPayment: number;
  bridgeTermMonths?: number;
}

interface EstimationData {
  monthlyPayment?: number;
  cashToClose?: number;
  interestRate?: number;
  loanAmount?: number;
}

interface ProductDetailComparisonSummaryProps {
  comparisons: Comparison[];
  estimations: EstimationData[];
}

export const ProductDetailComparisonSummary = ({
  comparisons,
  estimations,
}: ProductDetailComparisonSummaryProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(true);

  if (comparisons.length < 2) return null;

  const getLoanProgramColor = (program: USER_LOAN_PROGRAM) => {
    switch (program) {
      case USER_LOAN_PROGRAM.CONVENTIONAL:
        return theme.palette.primary.main;
      case USER_LOAN_PROGRAM.FHA:
        return '#4caf50';
      case USER_LOAN_PROGRAM.VA:
        return '#ff9800';
      case USER_LOAN_PROGRAM.USDA:
        return '#9c27b0';
      case USER_LOAN_PROGRAM.DSCR:
        return '#f44336';
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return '#e91e63';
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return '#795548';
      default:
        return '#757575';
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  const formatPercentage = (value?: number) => {
    if (!value) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  // Find the best option for each metric
  const getBestOption = (
    metric: 'monthlyPayment' | 'cashToClose' | 'interestRate'
  ) => {
    const values = estimations
      .map((est) => est[metric])
      .filter((val) => val !== undefined);
    if (values.length === 0) return -1;

    if (metric === 'interestRate') {
      return values.indexOf(Math.min(...values));
    } else {
      return values.indexOf(Math.min(...values));
    }
  };

  const bestMonthlyPayment = getBestOption('monthlyPayment');
  const bestCashToClose = getBestOption('cashToClose');
  const bestInterestRate = getBestOption('interestRate');

  const bd = theme.palette.greyShades.border1;
  const rowMuted = alpha(theme.palette.common.black, 0.04);
  const rowMutedHover = alpha(theme.palette.common.black, 0.08);
  const rowBest = alpha(theme.palette.success.main, 0.12);
  const successMain = theme.palette.success.main;
  const textPri = theme.palette.text.primary;
  const textSec = theme.palette.text.secondary;

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <GlassCard
      variant="medium"
      sx={{
        mb: 4,
        border: `1px solid ${bd}`,
        borderRadius: 4,
        color: 'text.primary',
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        transition: 'all 0.3s ease-in-out',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 4,
            pb: 2,
            borderBottom: `2px solid ${bd}`,
            cursor: 'pointer',
            '&:hover': {
              borderBottomColor: theme.palette.text.secondary,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon
              sx={{ mr: 2, color: 'primary.main', fontSize: 28 }}
            />
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Comparison Summary
            </Typography>
            <Chip
              label={`${comparisons.length} Options`}
              sx={{
                ml: 2,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              }}
            />
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {isMobile ? (
            // Mobile Table View
            <TableContainer
              component={MuiPaper}
              sx={{
                borderRadius: 2,
                border: `1px solid ${bd}`,
                boxShadow: 'none',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: textPri,
                        fontWeight: 600,
                        borderColor: bd,
                      }}
                    ></TableCell>
                    <TableCell
                      sx={{
                        color: textPri,
                        fontWeight: 600,
                        borderColor: bd,
                      }}
                    >
                      Monthly Payment
                    </TableCell>
                    <TableCell
                      sx={{
                        color: textPri,
                        fontWeight: 600,
                        borderColor: bd,
                      }}
                    >
                      Cash to Close
                    </TableCell>
                    <TableCell
                      sx={{
                        color: textPri,
                        fontWeight: 600,
                        borderColor: bd,
                      }}
                    >
                      Interest Rate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisons.map((comp, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor:
                          bestMonthlyPayment === idx ||
                          bestCashToClose === idx ||
                          bestInterestRate === idx
                            ? rowBest
                            : rowMuted,
                        '&:hover': {
                          bgcolor: rowMutedHover,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: textPri,
                          borderColor: bd,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {comp.loanProgram}
                          </Typography>
                          <Typography variant="caption" sx={{ color: textSec }}>
                            Down: ${comp.downPayment.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: textPri,
                          borderColor: bd,
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color={
                              bestMonthlyPayment === idx ? successMain : textPri
                            }
                          >
                            {formatCurrency(estimations[idx]?.monthlyPayment)}
                          </Typography>
                          {bestMonthlyPayment === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: textPri,
                          borderColor: bd,
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color={
                              bestCashToClose === idx ? successMain : textPri
                            }
                          >
                            {formatCurrency(estimations[idx]?.cashToClose)}
                          </Typography>
                          {bestCashToClose === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: textPri,
                          borderColor: bd,
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color={
                              bestInterestRate === idx ? successMain : textPri
                            }
                          >
                            {formatPercentage(estimations[idx]?.interestRate)}
                          </Typography>
                          {bestInterestRate === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // Desktop Grid View
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              {/* Monthly Payment Comparison */}
              <Grid item xs={12} md={4}>
                <GlassCard
                  variant="subtle"
                  sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: 3,
                    height: '100%',
                    position: 'relative',
                    border: `1px solid ${bd}`,
                    color: 'text.primary',
                    boxShadow: `0 2px 12px ${alpha(
                      theme.palette.common.black,
                      0.05
                    )}`,
                    transition: 'all 0.3s ease-in-out',
                    '@media (hover: hover)': {
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    mb={3}
                    color="text.primary"
                  >
                    💳 Monthly Payment
                  </Typography>
                  {comparisons.map((comp, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          background:
                            bestMonthlyPayment === idx ? rowBest : rowMuted,
                          borderRadius: 2,
                          border:
                            bestMonthlyPayment === idx
                              ? `2px solid ${successMain}`
                              : `1px solid ${bd}`,
                          position: 'relative',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {comp.loanProgram}
                          </Typography>
                          <Typography variant="caption" sx={{ color: textSec }}>
                            Down: ${comp.downPayment.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color={
                              bestMonthlyPayment === idx ? successMain : textPri
                            }
                          >
                            {formatCurrency(estimations[idx]?.monthlyPayment)}
                          </Typography>
                          {bestMonthlyPayment === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </GlassCard>
              </Grid>

              {/* Cash to Close Comparison */}
              <Grid item xs={12} md={4}>
                <GlassCard
                  variant="subtle"
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    border: `1px solid ${bd}`,
                    color: 'text.primary',
                    boxShadow: `0 2px 12px ${alpha(
                      theme.palette.common.black,
                      0.05
                    )}`,
                    transition: 'all 0.3s ease-in-out',
                    '@media (hover: hover)': {
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    mb={3}
                    color="text.primary"
                  >
                    💰 Cash to Close
                  </Typography>
                  {comparisons.map((comp, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          background:
                            bestCashToClose === idx ? rowBest : rowMuted,
                          borderRadius: 2,
                          border:
                            bestCashToClose === idx
                              ? `2px solid ${successMain}`
                              : `1px solid ${bd}`,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {comp.loanProgram}
                          </Typography>
                          <Typography variant="caption" sx={{ color: textSec }}>
                            LTV:{' '}
                            {(
                              (comp.downPayment /
                                (comp.downPayment +
                                  (estimations[idx]?.loanAmount || 0))) *
                              100
                            ).toFixed(1)}
                            %
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color={
                              bestCashToClose === idx ? successMain : textPri
                            }
                          >
                            {formatCurrency(estimations[idx]?.cashToClose)}
                          </Typography>
                          {bestCashToClose === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </GlassCard>
              </Grid>

              {/* Interest Rate Comparison */}
              <Grid item xs={12} md={4}>
                <GlassCard
                  variant="subtle"
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    border: `1px solid ${bd}`,
                    color: 'text.primary',
                    boxShadow: `0 2px 12px ${alpha(
                      theme.palette.common.black,
                      0.05
                    )}`,
                    transition: 'all 0.3s ease-in-out',
                    '@media (hover: hover)': {
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    mb={3}
                    color="text.primary"
                  >
                    📊 Interest Rate
                  </Typography>
                  {comparisons.map((comp, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          background:
                            bestInterestRate === idx ? rowBest : rowMuted,
                          borderRadius: 2,
                          border:
                            bestInterestRate === idx
                              ? `2px solid ${successMain}`
                              : `1px solid ${bd}`,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {comp.loanProgram}
                          </Typography>
                          <Typography variant="caption" sx={{ color: textSec }}>
                            {comp.loanProgram}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color={
                              bestInterestRate === idx ? successMain : textPri
                            }
                          >
                            {formatPercentage(estimations[idx]?.interestRate)}
                          </Typography>
                          {bestInterestRate === idx && (
                            <Chip
                              label="Best"
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: successMain,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </GlassCard>
              </Grid>
            </Grid>
          )}
        </Collapse>
      </CardContent>
    </GlassCard>
  );
};
